import { useState } from "react";
import { Link } from "react-router-dom";
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Camera, FileText, PencilLine, AlertTriangle, Check, X } from "lucide-react";
import { api, apiError, downloadSigned } from "@/lib/api";
import { ask } from "@/lib/confirm";
import { useIsMobile, CloseButton } from "@/components/shared/PagePrimitives";
import { PhotoThumb } from "@/components/shared/PhotoLightbox";
import { PdfTile } from "@/components/shared/PdfTile";
import { formatCZK, formatDate, formatDateShort, formatDateTime } from "@/lib/format";
import { STATUS, OPEN, MODES, dueText, deliveryText, projectLabel, todayISO, warnOf } from "@/lib/orders";
import { StatusPill, SupplierMark } from "./OrderBits";
import { OrderChain } from "./OrderChain";

const isPdf = (f) => /\.pdf($|\?)/i.test(f.url || "") || /\.pdf$/i.test(f.name || "");
const RET = { to_return: "k vrácení", returned: "vráceno", credited: "dobropis přijat" };
const plusDay = (iso, n) => { const d = new Date(`${iso}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };

function Box({ label, children, className = "" }) {
  return (
    <div className={`rounded-[16px] bg-secondary/60 p-3.5 min-w-0 ${className}`}>
      <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-1">{label}</div>
      {children}
    </div>
  );
}

// 388: soubor nahraný omylem jde odebrat (křížek v rohu, jen kancelář)
function Files({ files, onRemove }) {
  if (!files?.length) return null;
  return (
    <div className="flex flex-wrap gap-3 pt-1.5 pr-1.5">
      {files.map((f) => (
        <div key={f.id || f.url} className="relative self-start">
          {isPdf(f) ? <PdfTile url={f.url} name={f.name} testid="order-pdf" /> : <PhotoThumb url={f.url || f} />}
          {onRemove && (
            <button type="button" onClick={() => onRemove(f)} aria-label={`Odebrat ${f.name || "soubor"}`} title="Odebrat" data-testid="order-file-remove"
              className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-card border border-border shadow-sm grid place-items-center text-muted-foreground hover:text-destructive hover:border-destructive/50 before:absolute before:-inset-2 before:content-['']">
              <X size={14} strokeWidth={2.5} />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

// Detail objednávky (311): zprava na PC, zespodu na telefonu. Kancelář tu mění stav,
// platbu a vratky; stavba odsud přebírá s fotkou.
export function OrderDetail({ order, open, onOpenChange, canEdit, canReceive, onChanged, onEdit, onReceive }) {
  const mobile = useIsMobile();
  const [busy, setBusy] = useState(false);
  const [ret, setRet] = useState("");
  const [payForm, setPayForm] = useState(null);       // 329: { day: today|yday|custom, date } — kdy bylo zaplaceno
  if (!order) return null;
  const o = order;
  const today = todayISO();
  const isOpen = OPEN.includes(o.status);
  const money = o.amount_gross !== undefined;
  const warn = warnOf(o, today);
  const lines = o.lines || [];
  const pas = lines.filter((l) => l.pasport_item_id).length;
  const missing = new Set(o.problem?.line_ids || []);

  const run = async (req, ok) => {
    setBusy(true);
    try { const r = await req; onChanged?.(r.data); if (ok) toast.success(ok); return r.data; }
    catch (err) { toast.error(apiError(err)); return null; }
    finally { setBusy(false); }
  };
  const patch = (body, ok) => run(api.patch(`/orders/${o.id}`, body), ok);
  const cancel = async () => {
    if (!(await ask(`Zrušit objednávku ${o.supplier_name}?${pas ? " Položky v pasportu se vrátí do „K objednání“." : ""}`, { ok: "Zrušit objednávku", cancel: "Zpět", danger: true }))) return;
    run(api.post(`/orders/${o.id}/cancel`, {}), "Objednávka zrušena.");
  };
  const removeFile = async (f, receipt) => {
    const last = receipt && (o.received?.photos || []).length === 1;
    const nm = f.name || "soubor";
    const keep = o.document ? " Faktura v Dokladech zůstane." : "";
    const msg = last
      ? `Odebrat ${nm}? Je to jediný doklad k převzetí, takže se převzetí zruší a objednávka se vrátí mezi rozpracované. Pak ji převezmete znovu se správným dokladem.${keep}`
      : `Odebrat ${nm} z objednávky?${keep}`;
    if (!(await ask(msg, { ok: last ? "Odebrat a zrušit převzetí" : "Odebrat", cancel: "Zpět", danger: true }))) return;
    run(api.post(`/orders/${o.id}/files/remove`, { id: f.id, url: f.url }), last ? "Převzetí zrušeno, objednávka je zpět mezi rozpracovanými." : "Soubor odebrán.");
  };
  const addReturn = async () => {
    if (!ret.trim()) return;
    if (await run(api.post(`/orders/${o.id}/returns`, { text: ret }), "Vratka zapsána.")) setRet("");
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side={mobile ? "bottom" : "right"} hideClose onOpenAutoFocus={(e) => e.preventDefault()} data-testid="order-detail"
        className={mobile ? "p-0 h-[94dvh] rounded-t-[24px] flex flex-col pb-0"
          : "p-0 border-0 md:inset-y-3 md:right-3 md:h-auto md:rounded-[24px] overflow-hidden flex flex-col w-[min(540px,calc(100vw-24px))] sm:max-w-none"}>
        <SheetTitle className="sr-only">Objednávka {o.supplier_name}</SheetTitle>
        <SheetDescription className="sr-only">Detail objednávky</SheetDescription>
        {mobile && <div className="mx-auto mt-2 h-1.5 w-10 rounded-full bg-border shrink-0" aria-hidden />}
        <div className="shrink-0 flex items-start gap-3 px-4 md:px-5 pt-4 pb-3">
          <SupplierMark name={o.supplier_name} size={44} />
          <div className="flex-1 min-w-0">
            <h2 className="font-heading font-extrabold text-[20px] leading-tight truncate" data-testid="order-detail-title">{o.supplier_name} · {o.title}</h2>
            <p className="text-[13px] text-muted-foreground mt-0.5">
              {[o.order_no ? `č. ${o.order_no}` : "bez čísla", projectLabel(o), o.created_by_name && `objednal(a) ${o.created_by_name} ${formatDateShort(o.created_at)}`].filter(Boolean).join(" · ")}
            </p>
          </div>
          {canEdit && o.status !== "cancelled" && (
            <button type="button" onClick={() => onEdit(o)} aria-label="Upravit objednávku" data-testid="order-edit"
              className="w-10 h-10 rounded-full grid place-items-center bg-secondary hover:bg-secondary/70 shrink-0"><PencilLine size={17} /></button>
          )}
          <SheetClose asChild><CloseButton testid="order-detail-close" /></SheetClose>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 md:px-5 pb-6 space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusPill o={o} />
            {warn && <span className={`text-[13px] font-semibold ${warn.tone === "red" ? "text-destructive" : "text-amber-700 dark:text-amber-400"}`}>{warn.text}</span>}
          </div>
          {o.status !== "cancelled" && (
            <OrderChain ordered={formatDateShort(o.created_at)} received={o.status === "received"} note={(o.received?.photos || []).length > 0}
              invoice={o.document ? (o.document.number || "spárováno") : null} paid={o.paid ? formatDateShort(o.paid_at) : null} />
          )}

          {o.problem && (
            <div className="rounded-[16px] border border-destructive/40 bg-destructive/[.07] p-3.5" data-testid="order-problem">
              <div className="flex items-center gap-2 font-bold text-destructive"><AlertTriangle size={17} />{o.problem.kind === "damaged" ? "Poškozené" : "Něco chybí"}</div>
              {o.problem.note && <p className="text-[14px] mt-1">{o.problem.note}</p>}
              <p className="text-[12.5px] text-muted-foreground mt-1">nahlásil(a) {o.problem.by_name} {formatDateShort(o.problem.at)}</p>
              {canEdit && <Button variant="outline" size="sm" disabled={busy} onClick={() => patch({ problem: null }, "Problém vyřešen.")} className="mt-2 h-9 font-bold" data-testid="order-problem-solved">
                <Check size={15} /> Vyřešeno</Button>}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <Box label="Dodání">
              <div className="font-bold text-[15px]">{MODES[o.delivery?.mode || "site"].label}</div>
              <div className="text-[13.5px]">{dueText(o, today)}{o.due_note ? ` · ${o.due_note}` : ""}</div>
              <div className="text-[12.5px] text-muted-foreground truncate">{deliveryText(o)}{o.receiver_name ? ` · ${o.delivery?.mode === "pickup" ? "pojede" : "převezme"} ${o.receiver_name}` : ""}</div>
            </Box>
            <Box label="Platba">
              <div className={`font-bold text-[15px] ${o.paid ? "text-success" : ""}`}>{o.paid ? `Zaplaceno ${formatDate(o.paid_at)}` : "Nezaplaceno"}</div>
              {o.document ? (
                // 329: spárovaná s fakturou — platba se bere z ní, zadává se jednou
                <div className="mt-0.5 space-y-0.5" data-testid="order-paid-from-invoice">
                  <div className="text-[12.5px] text-muted-foreground">z faktury{o.document.number ? ` č. ${o.document.number}` : ""}{!o.paid && o.document.due_date ? ` · splatnost ${formatDateShort(o.document.due_date)}` : ""}</div>
                  {o.document.id && <Link to={`/ekonomika/doklady?open=faktura:${o.document.id}`} className="text-[13px] font-bold text-brand hover:underline" data-testid="order-open-invoice">otevřít fakturu</Link>}
                  {canEdit && <div><button type="button" disabled={busy} onClick={() => run(api.post(`/orders/${o.id}/unlink`), "Spárování zrušeno.")} data-testid="order-unlink"
                    className="text-[12.5px] font-semibold text-muted-foreground hover:underline">zrušit spárování</button></div>}
                </div>
              ) : payForm ? (
                <div className="mt-1.5 space-y-2" data-testid="order-pay-form">
                  <div className="text-[12.5px] font-semibold">Kdy bylo zaplaceno?</div>
                  <div className="flex flex-wrap gap-1.5">
                    {[["today", "Dnes"], ["yday", "Včera"], ["custom", "Jiný den"]].map(([k, l]) => (
                      <button key={k} type="button" aria-pressed={payForm.day === k} onClick={() => setPayForm({ ...payForm, day: k })} data-testid={`order-pay-${k}`}
                        className={`h-9 px-3 rounded-full text-[13px] font-bold border ${payForm.day === k ? "bg-foreground text-background border-foreground" : "bg-card border-border"}`}>{l}</button>
                    ))}
                  </div>
                  {payForm.day === "custom" && <input type="date" max={today} value={payForm.date} onChange={(e) => setPayForm({ ...payForm, date: e.target.value })}
                    className="h-10 w-full min-w-0 rounded-[12px] border border-border bg-card px-3 text-base md:text-sm" data-testid="order-pay-date" aria-label="Datum platby" />}
                  <div className="flex gap-2">
                    <Button size="sm" disabled={busy || (payForm.day === "custom" && !payForm.date)} data-testid="order-pay-save" className="h-9 rounded-full font-bold"
                      onClick={async () => {
                        const day = payForm.day === "today" ? today : payForm.day === "yday" ? plusDay(today, -1) : payForm.date;
                        if (await patch({ paid: true, paid_on: day }, "Platba zapsána.")) setPayForm(null);
                      }}>Uložit</Button>
                    <Button size="sm" variant="outline" onClick={() => setPayForm(null)} className="h-9 rounded-full font-bold">Zrušit</Button>
                  </div>
                </div>
              ) : canEdit && o.status !== "cancelled" && (
                <div className="flex flex-wrap gap-x-3">
                  <button type="button" disabled={busy} data-testid="order-paid-toggle"
                    onClick={() => (o.paid ? patch({ paid: false }, "Platba zrušena.") : setPayForm({ day: "today", date: today }))}
                    className="text-[13px] font-bold text-brand hover:underline mt-0.5">{o.paid ? "zrušit platbu" : "označit jako zaplacené"}</button>
                  {o.paid && <button type="button" disabled={busy} data-testid="order-paid-change" onClick={() => setPayForm({ day: "custom", date: String(o.paid_at || today).slice(0, 10) })}
                    className="text-[13px] font-bold text-muted-foreground hover:underline mt-0.5">změnit datum</button>}
                </div>
              )}
              {!canEdit && o.delivery?.mode === "pickup" && !o.paid && <div className="text-[12.5px] text-muted-foreground">platí se při vyzvednutí</div>}
            </Box>
          </div>

          {money && o.amount_gross != null && (
            <Box label="K úhradě s DPH">
              <div className="font-heading font-extrabold text-[30px] leading-none tabular-nums" data-testid="order-amount">{formatCZK(o.amount_gross)}</div>
              <div className="text-[13px] text-muted-foreground mt-1 tabular-nums">bez DPH {formatCZK(o.amount_net)} · DPH {o.vat_rate} % · do nákladů {o.project_id ? projectLabel(o) : "režie"} až z faktury</div>
            </Box>
          )}

          {canEdit && isOpen && (
            <section>
              <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Stav</h3>
              <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Stav objednávky">
                {OPEN.map((s) => (
                  <button key={s} type="button" role="radio" aria-checked={o.status === s} disabled={busy || o.status === s}
                    onClick={() => patch({ status: s }, `Stav: ${STATUS[s].label.toLowerCase()}`)} data-testid={`order-status-${s}`}
                    className={`h-9 px-3.5 rounded-full text-[13px] font-bold border transition-colors ${o.status === s ? "bg-foreground text-background border-foreground" : "bg-card border-border hover:border-brand"}`}>
                    {STATUS[s].label}
                  </button>
                ))}
              </div>
            </section>
          )}

          {o.received && (
            <section data-testid="order-received">
              <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Převzetí</h3>
              <p className="text-[14px] mb-2">{o.received.by_name} · {formatDateTime(o.received.at)}{o.received.note ? ` · ${o.received.note}` : ""}</p>
              <Files files={(o.received.photos || []).map((p) => (typeof p === "string" ? { url: p } : p))}
                onRemove={canEdit && !busy ? (f) => removeFile(f, true) : null} />
            </section>
          )}

          {lines.length > 0 && (
            <section>
              <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
                {pas ? `Položky z pasportu ${pas} · stav se propíše do pasportu` : `Položky ${lines.length}`}</h3>
              <ul className="rounded-[16px] border border-border divide-y divide-border/70" data-testid="order-lines">
                {lines.map((l) => (
                  <li key={l.id} className={`flex items-center gap-2 px-3 py-2 text-[13.5px] ${missing.has(l.id) ? "bg-destructive/[.06]" : ""}`}>
                    {l.room && <span className="text-muted-foreground shrink-0">{l.room}</span>}
                    <span className="flex-1 min-w-0 truncate font-semibold">{l.name}</span>
                    {missing.has(l.id) && <span className="text-[12px] font-bold text-destructive">chybí</span>}
                    <span className="tabular-nums text-muted-foreground shrink-0">{String(l.qty ?? 1).replace(".", ",")} {l.unit || "ks"}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {(o.files || []).length > 0 && (
            <section>
              <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Přílohy</h3>
              <Files files={o.files} onRemove={canEdit && o.status !== "cancelled" && !busy ? (f) => removeFile(f, false) : null} />
            </section>
          )}

          {o.note && (
            <section>
              <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">Poznámka</h3>
              <p className="text-[14px] whitespace-pre-line">{o.note}</p>
            </section>
          )}

          {o.status === "received" && (canEdit || (o.returns || []).length > 0) && (
            <section data-testid="order-returns">
              <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Vratky</h3>
              <ul className="space-y-1.5">
                {(o.returns || []).map((r) => (
                  <li key={r.id} className="flex items-center gap-2 text-[14px]">
                    <span className="flex-1 min-w-0">{r.text}</span>
                    {canEdit ? (
                      <select value={r.status} disabled={busy} aria-label="Stav vratky"
                        onChange={(e) => run(api.patch(`/orders/${o.id}/returns/${r.id}`, { status: e.target.value }))}
                        className="h-8 rounded-full bg-secondary px-2.5 text-[12.5px] font-semibold">
                        {Object.entries(RET).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
                      </select>
                    ) : <span className="text-[12.5px] text-muted-foreground">{RET[r.status]}</span>}
                  </li>
                ))}
              </ul>
              {canEdit && (
                <div className="flex gap-2 mt-2">
                  <Input value={ret} onChange={(e) => setRet(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addReturn(); }}
                    placeholder="Co se vrací, např. 2× klika – špatná barva" data-testid="order-return-text" className="h-10 flex-1 min-w-0 text-base md:text-sm" />
                  <Button variant="outline" onClick={addReturn} disabled={busy || !ret.trim()} className="h-10 font-bold" data-testid="order-return-add">Vratka</Button>
                </div>
              )}
            </section>
          )}

          {(o.history || []).length > 0 && (
            <section>
              <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Průběh</h3>
              <ol className="relative border-l border-border ml-1.5 space-y-2.5" data-testid="order-history">
                {[...o.history].reverse().map((h, i) => (
                  <li key={i} className="pl-4 relative">
                    <i className={`absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full ${i === 0 ? "bg-brand" : "bg-border"}`} aria-hidden />
                    <div className="text-[14px] font-semibold">{h.text}</div>
                    <div className="text-[12px] text-muted-foreground">{formatDateTime(h.at)}{h.by_name ? ` · ${h.by_name}` : ""}</div>
                  </li>
                ))}
              </ol>
            </section>
          )}
        </div>

        {(isOpen && (canReceive || canEdit)) && (
          <div className="shrink-0 border-t border-border px-4 md:px-5 py-3 flex flex-col-reverse md:flex-row gap-2 md:justify-end pb-[max(12px,env(safe-area-inset-bottom))] md:pb-3">
            {canEdit && <Button variant="ghost" onClick={cancel} disabled={busy} className="w-full md:w-auto h-12 md:h-11 font-bold text-destructive hover:text-destructive md:mr-auto" data-testid="order-cancel">Zrušit objednávku</Button>}
            {canReceive && <Button onClick={() => onReceive(o)} disabled={busy} className="w-full md:w-auto h-12 md:h-11 font-bold px-6" data-testid="order-receive">
              <Camera size={17} /> {o.delivery?.mode === "pickup" ? "Vyzvednuto" : "Převzít"}</Button>}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
